import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithCredential, type Auth } from "firebase/auth";
import { devLog } from "./dev-log";

// ============================================================================
// Firebase Singleton
// ============================================================================

interface FirebaseInstance {
  auth: Auth;
  googleProvider: GoogleAuthProvider;
}

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let googleProvider: GoogleAuthProvider | null = null;

export function initializeFirebase(): FirebaseInstance {
  if (firebaseApp && firebaseAuth && googleProvider) {
    // Already initialized
    return { auth: firebaseAuth, googleProvider };
  }

  // Firebase configuration from environment variables
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  // Validate required Firebase config
  const requiredEnvVars = [
    "VITE_FIREBASE_API_KEY",
    "VITE_FIREBASE_AUTH_DOMAIN",
    "VITE_FIREBASE_PROJECT_ID",
    "VITE_FIREBASE_APP_ID",
  ];

  const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);
  if (missingVars.length > 0) {
    console.error(`Missing required Firebase environment variables: ${missingVars.join(", ")}`);
    throw new Error(`Firebase configuration incomplete. Missing: ${missingVars.join(", ")}`);
  }

  // Initialize Firebase
  firebaseApp = initializeApp(firebaseConfig);
  firebaseAuth = getAuth(firebaseApp);
  googleProvider = new GoogleAuthProvider();

  // Configure Google provider
  googleProvider.addScope("profile");
  googleProvider.addScope("email");

  return { auth: firebaseAuth, googleProvider };
}

// ============================================================================
// Unified Login Helpers
// ============================================================================

export interface FirebaseLoginResult {
  idToken: string;
  email?: string;
}

// Aliases for different social providers (all use Firebase now)
export type GoogleLoginResult = FirebaseLoginResult;

export async function performGoogleLogin(): Promise<GoogleLoginResult> {
  const { auth, googleProvider } = initializeFirebase();
  const result = await signInWithPopup(auth, googleProvider);

  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email || undefined,
  };
}

/**
 * Sign in with Google ID Token (from One Tap or other sources)
 * Bridge function to exchange Google ID Token for Firebase Credential
 */
export async function signInWithGoogleIdToken(idToken: string): Promise<GoogleLoginResult> {
  const { auth } = initializeFirebase();

  // Create a credential from the ID token
  const credential = GoogleAuthProvider.credential(idToken);

  // Sign in with the credential
  const result = await signInWithCredential(auth, credential);

  // Get a fresh ID token from Firebase user
  const firebaseIdToken = await result.user.getIdToken();

  return {
    idToken: firebaseIdToken,
    email: result.user.email || undefined,
  };
}

/**
 * Facebook JavaScript SDK integration
 * Official Facebook SDK for web applications
 */

interface FacebookUserInfo {
  id: string;
  name: string;
  email?: string;
  picture?: {
    data: {
      url: string;
    };
  };
  error?: any;
}

interface FacebookLoginResponse {
  accessToken: string;
  userID: string;
  userInfo: FacebookUserInfo;
  status: string;
}

class FacebookSDKManager {
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private appId: string;

  constructor() {
    this.appId = import.meta.env.VITE_FACEBOOK_APP_ID;
    if (!this.appId) {
      console.error(
        "❌ Facebook App ID not configured. Please set VITE_FACEBOOK_APP_ID in your environment variables."
      );
    }
  }

  /**
   * Initialize Facebook SDK
   */
  init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    if (!this.appId) {
      return Promise.reject(new Error("Facebook App ID not configured"));
    }

    this.initPromise = new Promise((resolve, reject) => {
      try {
        // Check if the SDK is already loaded
        if (window.FB && this.isInitialized) {
          resolve();
          return;
        }

        // Set asynchronous initialization callback
        window.fbAsyncInit = () => {
          window.FB.init({
            appId: this.appId,
            cookie: true,
            xfbml: true,
            version: "v18.0",
          });

          this.isInitialized = true;
          devLog("✅ Facebook SDK initialized successfully");
          resolve();
        };

        // Dynamically load Facebook SDK
        this.loadSDKScript().catch(reject);
      } catch (error) {
        console.error("❌ Error initializing Facebook SDK:", error);
        reject(error);
      }
    });

    return this.initPromise;
  }

  /**
   * Dynamically load Facebook SDK script
   */
  private loadSDKScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if the script is already loaded
      if (document.getElementById("facebook-jssdk")) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";

      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Facebook SDK"));

      document.head.appendChild(script);
    });
  }

  /**
   * Facebook login
   */
  async login(): Promise<FacebookLoginResponse> {
    await this.init();

    return new Promise((resolve, reject) => {
      window.FB.login(
        (response: any) => {
          devLog("📱 Facebook login response:", response);

          if (response.status === "connected" && response.authResponse) {
            // 获取用户信息
            window.FB.api("/me", { fields: "id,name,email,picture" }, (userInfo: FacebookUserInfo) => {
              if (userInfo && !userInfo.error) {
                devLog("👤 Facebook user info:", userInfo);

                resolve({
                  accessToken: response.authResponse.accessToken,
                  userID: response.authResponse.userID,
                  userInfo,
                  status: response.status,
                });
              } else {
                console.error("❌ Failed to get user info:", userInfo);
                reject(new Error("Failed to get user information"));
              }
            });
          } else if (response.status === "not_authorized") {
            devLog("🚫 User cancelled login or did not fully authorize");
            reject(new Error("User cancelled login or did not fully authorize"));
          } else {
            devLog("❌ User cancelled login");
            reject(new Error("User cancelled login"));
          }
        },
        {
          scope: "public_profile,email",
          return_scopes: true,
        }
      );
    });
  }
}

// Export singleton instance
export const facebookSDK = new FacebookSDKManager();

// Export types
export type { FacebookLoginResponse, FacebookUserInfo };
